// src/lib/shareApi.ts
/**
 * T-21: Share API Client (Frontend)
 * 
 * Client functions for interacting with the PHP backend API.
 * Integrates with T-20 serialization for scene sharing workflow.
 */

// Type declaration for Vite's import.meta.env
declare global {
  interface ImportMeta {
    env: {
      DEV: boolean;
      PROD: boolean;
      MODE: string;
    };
  }
}

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * Configuration for the Share API
 */
interface ShareApiConfig {
    baseUrl: string;
    timeout: number;
    retries: number;
  }
  
  /**
   * Response from POST /api/scenes
   */
  export interface PostSceneResponse {
    id: string;
    created_at: string;
    size_bytes: number;
    schema_version: number;
  }
  
  /**
   * Response from GET /api/scenes
   */
  export interface GetSceneResponse {
    scene: string; // Base64 compressed data
    metadata: {
      id: string;
      created_at: string;
      schema_version: number;
      size_bytes: number;
      access_count: number;
    };
  }
  
  /**
   * API Error response
   */
  export interface ApiError {
    error: string;
    details?: any;
  }
  
  /**
   * Share operation result
   */
  export interface ShareResult<T = any> {
    success: boolean;
    data?: T;
    error?: string;
    statusCode?: number;
  }
  
  // =============================================================================
  // CONFIGURATION
  // =============================================================================
  
  /**
   * Get API configuration based on environment
   */
  function getApiConfig(): ShareApiConfig {
    const isDevelopment = import.meta.env.DEV;
    
    return {
      baseUrl: isDevelopment 
        ? 'http://localhost:8000'  // Just the base URL
        : 'https://numline.com/api',
      timeout: 30000,
      retries: 3,
    };
  }
  
  // =============================================================================
  // CORE API FUNCTIONS
  // =============================================================================
  
  /**
   * POST compressed scene data to the API
   * Integrates with T-20 compression output
   */
  export async function postScene(compressed: string): Promise<PostSceneResponse> {
    console.log('postScene called with compressed data length:', compressed.length);
    
    if (!compressed || typeof compressed !== 'string') {
      throw new Error('Invalid compressed data: must be non-empty string');
    }
    
    // Validate that this looks like base64 compressed data
    if (!/^[A-Za-z0-9+/=]+$/.test(compressed)) {
      throw new Error('Invalid compressed data: not valid base64');
    }
    
    const requestBody = JSON.stringify({ scene: compressed });
    console.log('Request body size:', new Blob([requestBody]).size);
    console.log('Request body preview:', requestBody.substring(0, 200) + '...');
    
    // Check request size
    const requestSize = new Blob([requestBody]).size;
    if (requestSize > 300000) { // ~300KB limit (allowing for base64 overhead)
      throw new Error('Scene too large for sharing (max ~200KB)');
    }
    
      const result = await makeApiRequest<PostSceneResponse>('POST', '/scenes.php', {
    body: requestBody,
    headers: {
      'Content-Type': 'application/json',
    },
  });
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to save scene');
    }
    
    return result.data;
  }
  
  /**
   * GET scene data by ID from the API
   * Returns compressed data ready for T-20 decompression
   */
  export async function loadScene(id: string): Promise<GetSceneResponse> {
    if (!id || typeof id !== 'string') {
      throw new Error('Invalid scene ID: must be non-empty string');
    }
    
    // Validate ID format (12-character hex)
    if (!/^[a-f0-9]{12}$/.test(id)) {
      throw new Error('Invalid scene ID format: must be 12-character hex string');
    }
    
    const result = await makeApiRequest<GetSceneResponse>('GET', `/scenes.php?id=${encodeURIComponent(id)}`);
    
    if (!result.success || !result.data) {
      if (result.statusCode === 404) {
        throw new Error('Scene not found - it may have been deleted or the ID is incorrect');
      }
      throw new Error(result.error || 'Failed to load scene');
    }
    
    return result.data;
  }
  
  // =============================================================================
  // HTTP CLIENT IMPLEMENTATION
  // =============================================================================
  
  /**
   * Make HTTP request to the API with retry logic
   */
  async function makeApiRequest<T>(
    method: 'GET' | 'POST', 
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ShareResult<T>> {
    const config = getApiConfig();
    const url = `${config.baseUrl}${endpoint}`;
    
    console.log(`Making ${method} request to:`, url);
    console.log('Request options:', options);
    
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= config.retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), config.timeout);
        
        const response = await fetch(url, {
          method,
          signal: controller.signal,
          ...options,
          headers: {
            'Accept': 'application/json',
            ...options.headers,
          },
        });
        
        clearTimeout(timeoutId);
        
        console.log(`Response status: ${response.status} ${response.statusText}`);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        // Parse response
        let responseData: any;
        const contentType = response.headers.get('content-type');
        
        console.log('Content-Type:', contentType);
        
        // Get the raw response text first for debugging
        const responseText = await response.text();
        console.log('Raw response text:', responseText);
        
        if (contentType && contentType.includes('application/json')) {
          try {
            responseData = JSON.parse(responseText);
            console.log('Response data:', responseData);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            console.log('Failed to parse response as JSON:', responseText);
            responseData = { error: 'Invalid JSON response' };
          }
        } else {
          responseData = { error: 'Invalid response format' };
          console.log('Non-JSON response, using error fallback');
        }
        
        // Handle HTTP errors
        if (!response.ok) {
          console.log('HTTP error response:', responseData);
          console.log('Error details:', JSON.stringify(responseData, null, 2));
          return {
            success: false,
            error: responseData.error || `HTTP ${response.status}: ${response.statusText}`,
            statusCode: response.status,
          };
        }
        
        // Success
        console.log('Successful response:', responseData);
        return {
          success: true,
          data: responseData as T,
          statusCode: response.status,
        };
        
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        // Don't retry on certain errors
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            break; // Timeout - don't retry
          }
          if (error.message.includes('Failed to fetch')) {
            // Network error - only retry if not the last attempt
            if (attempt === config.retries) break;
            
            // Exponential backoff
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
            continue;
          }
        }
        
        break; // Don't retry other types of errors
      }
    }
    
    return {
      success: false,
      error: lastError?.message || 'Unknown error occurred',
    };
  }
  
  // =============================================================================
  // UTILITY FUNCTIONS
  // =============================================================================
  
  /**
   * Test API connectivity
   */
  export async function testApiConnection(): Promise<boolean> {
      try {
    const config = getApiConfig();
    const response = await fetch(`${config.baseUrl}/scenes.php?status=check`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });
      
      return response.ok;
    } catch (error) {
      console.error('API connection test failed:', error);
      return false;
    }
  }
  
  /**
   * Get API status information
   */
  export async function getApiStatus(): Promise<any> {
      try {
    const config = getApiConfig();
    const response = await fetch(`${config.baseUrl}/scenes.php?status=check`);
      
      if (response.ok) {
        return await response.json();
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get API status:', error);
      return null;
    }
  }
  
  /**
   * Generate shareable URL for a scene ID
   */
  export function generateShareUrl(sceneId: string, baseUrl?: string): string {
    if (!sceneId || !/^[a-f0-9]{12}$/.test(sceneId)) {
      throw new Error('Invalid scene ID');
    }
    
    const currentUrl = baseUrl || window.location.origin;
    return `${currentUrl}?share=${sceneId}`;
  }
  
  /**
   * Extract scene ID from share URL
   */
  export function extractSceneIdFromUrl(url?: string): string | null {
    try {
      const urlToCheck = url || window.location.href;
      const urlObj = new URL(urlToCheck);
      const sceneId = urlObj.searchParams.get('share');
      
      if (sceneId && /^[a-f0-9]{12}$/.test(sceneId)) {
        return sceneId;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
  
  // =============================================================================
  // INTEGRATION WITH T-20 SERIALIZATION
  // =============================================================================
  
  /**
   * Complete share workflow: serialize → compress → upload → return share URL
   * Integrates T-20 serialization with T-21 API
   */
  export async function shareScene(state: any): Promise<{
    id: string;
    url: string;
    metadata: PostSceneResponse;
  }> {
    // Import T-20 functions dynamically to avoid circular dependencies
    const { serialize, compress } = await import('./serialization');
    
    try {
      console.log('Starting share workflow with state:', state);
      
      // Step 1: Serialize Redux state (T-20)
      const exported = serialize(state);
      console.log('Serialized state:', exported);
      
      // Step 2: Compress to base64 string (T-20)
      const compressed = compress(exported);
      console.log('Compressed data length:', compressed.length);
      
      // Step 3: Upload to API (T-21)
      const response = await postScene(compressed);
      console.log('API response:', response);
      
      // Step 4: Generate share URL
      const shareUrl = generateShareUrl(response.id);
      
      return {
        id: response.id,
        url: shareUrl,
        metadata: response,
      };
    } catch (error) {
      console.error('Share workflow failed:', error);
      throw new Error(`Failed to share scene: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Complete load workflow: download → decompress → hydrate → return state
   * Integrates T-21 API with T-20 serialization
   */
  export async function loadSharedScene(sceneId: string): Promise<{
    state: any;
    metadata: GetSceneResponse['metadata'];
  }> {
    // Import T-20 functions dynamically
    const { decompress, hydrate } = await import('./serialization');
    
    try {
      // Step 1: Download from API (T-21)
      const response = await loadScene(sceneId);
      
      // Step 2: Decompress scene data (T-20)
      const exported = decompress(response.scene);
      
      // Step 3: Hydrate to Redux state format (T-20)
      const state = hydrate(exported);
      
      return {
        state,
        metadata: response.metadata,
      };
    } catch (error) {
      throw new Error(`Failed to load shared scene: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  // =============================================================================
  // ERROR HANDLING UTILITIES
  // =============================================================================
  
  /**
   * Check if an error is a network error
   */
  export function isNetworkError(error: Error): boolean {
    return error.message.includes('Failed to fetch') ||
           error.message.includes('Network request failed') ||
           error.message.includes('fetch');
  }
  
  /**
   * Check if an error is a rate limit error
   */
  export function isRateLimitError(error: Error): boolean {
    return error.message.includes('rate limit') ||
           error.message.includes('too many');
  }
  
  /**
   * Get user-friendly error message
   */
  export function getUserFriendlyErrorMessage(error: Error): string {
    if (isNetworkError(error)) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (isRateLimitError(error)) {
      return 'You\'ve made too many requests. Please wait a moment and try again.';
    }
    
    if (error.message.includes('Scene not found')) {
      return 'This shared scene could not be found. It may have been deleted or the link is incorrect.';
    }
    
    if (error.message.includes('too large')) {
      return 'Your scene is too complex to share. Try removing some elements and try again.';
    }
    
    // Default message
    return 'An unexpected error occurred. Please try again.';
  }